import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Base64 } from 'js-base64'
import { FiAlertCircle } from 'react-icons/fi'
import Collapse from '@mui/material/Collapse'
import { Formik, Form, FormikHelpers } from 'formik'

import usePersistedState from '../../hooks/usePersistedState'
import api from '../../services/api'
import InputSMSCode from '../../components/InputSMSCode'
import Button from '../../components/Button'
import Header from '../../components/Header'
import {
  ConfigData,
  ErroProps,
  UserData,
  Participant,
  ServiceTimeData,
} from '../../utils/interfaces'

type ConfirmSMSFormValues = {
  codeSMS: string
}

type ApiDependente = {
  nome: string
  nascimento: string
  cpf: string
  rg: string
  possuiDeficiencia: string
  dcrDeficiencia: string
  grauParentesco: string
}

type ApiTempoServico = {
  dcrOrganizacao: string
  responsavelTempo: string
  magisterio: string
  saude: string
  regimePrevidenciario: string
  tipoVinculo: string
  naturezaJuridica: string
  regulamentado: string
  dataAdmissao: string
  dataDemissao: string
  dcrCargo: string
}

type ApiUserResponse = {
  versao: number
  nome: string
  cpf: string
  nascimento: string
  email: string
  telefone: string
  rg: string
  lugarNascimento: string
  possuiDeficiencia: string
  dcrDeficiencia: string
  filiacao1: string
  filiacao2: string
  estadoCivil: string
  genero: string
  dependentes?: ApiDependente[]
  temposServico?: ApiTempoServico[]
}

function normalizeCpf(cpf?: string) {
  return (cpf ?? '').replace(/\./g, '').replace('-', '')
}

function mapUserData(apiData: ApiUserResponse): UserData {
  const firstTime = apiData.temposServico?.[0]

  return {
    cpf: apiData.cpf ?? '',
    admission: firstTime?.dataAdmissao ?? '',
    name: apiData.nome ?? '',
    birthdate: apiData.nascimento ?? '',
    email: apiData.email ?? '',
    phone: apiData.telefone ?? '',
    patrocinadora: '',
    dcrPatrocinadora: firstTime?.dcrOrganizacao ?? '',
    matricula: '',
    parental1: apiData.filiacao1 ?? '',
    parental2: apiData.filiacao2 ?? '',
    birthPlace: apiData.lugarNascimento ?? '',
    rg: apiData.rg ?? '',
    maritalState: apiData.estadoCivil ?? '',
    gender: apiData.genero ?? '',
    deficiency: apiData.possuiDeficiencia ?? '',
    deficiencyDcr: apiData.dcrDeficiencia ?? '',
  }
}

function mapParticipants(dependentes: ApiDependente[] = []): Participant[] {
  return dependentes.map((dep) => ({
    name: dep.nome ?? '',
    cpf: dep.cpf ?? '',
    birthdate: dep.nascimento ?? '',
    rg: dep.rg ?? '',
    hasDeficiency: dep.possuiDeficiencia ?? '',
    deficiencyDcr: dep.dcrDeficiencia ?? '',
    grauParentesco: dep.grauParentesco ?? '',
  }))
}

function mapServiceTime(tempos: ApiTempoServico[] = []): ServiceTimeData[] {
  return tempos.map((t) => ({
    organizationDcr: t.dcrOrganizacao ?? '',
    timeResponsible: t.responsavelTempo ?? '',
    teaching: t.magisterio ?? '',
    health: t.saude ?? '',
    pensionSystem: t.regimePrevidenciario ?? '',
    typeLink: t.tipoVinculo ?? '',
    legalNature: t.naturezaJuridica ?? '',
    regulamented: t.regulamentado ?? '',
    startDate: t.dataAdmissao ?? '',
    endDate: t.dataDemissao ?? '',
    positionDcr: t.dcrCargo ?? '',
  }))
}

const ConfirmSMS: React.FC = () => {
  const [userData, setUserData] = usePersistedState<UserData>(
    'userData',
    {} as UserData,
  )

  const [configData, setConfigData] = usePersistedState<ConfigData>(
    'configData',
    {} as ConfigData,
  )

  const [, setErroProps] = usePersistedState<ErroProps>(
    'erroProps',
    {} as ErroProps,
  )

  const [, setParticipantsGroup] = usePersistedState<Participant[]>(
    'participantsGroup',
    [] as Participant[],
  )

  const [, setServiceTimeGroup] = usePersistedState<ServiceTimeData[]>(
    'servicestimeGroup',
    [] as ServiceTimeData[],
  )

  const [reSend, setReSend] = useState(false)
  const [open, setOpen] = useState(false)
  const [sizeCode] = useState(4)
  const [timer, setTimer] = useState(60)

  const navigate = useNavigate()
  const [tipoContato] = usePersistedState('tipoContato', 'S')
  const contato = tipoContato === 'S' ? 'SMS' : 'e-mail'

  const lastSubmittedCodeRef = useRef<string>('')

  const initialValues = useMemo<ConfirmSMSFormValues>(
    () => ({ codeSMS: '' }),
    [],
  )

  const isSubmittingRef = useRef(false)

  const handleSubmit = useCallback(
    async (
      data: ConfirmSMSFormValues,
      helpers: FormikHelpers<ConfirmSMSFormValues>,
    ) => {
      try {
        const code = (data.codeSMS ?? '').trim()
        if (code.length !== sizeCode) return
        if (isSubmittingRef.current) return

        isSubmittingRef.current = true
        helpers.setSubmitting(true)

        const parametros = Base64.encode(
          `{"token": "${configData.token}",
          "codigoValidador": "${code}",
          "cliente": "${configData.codCliente}"}`,
        )
        const parametrosFinal = Base64.encode(parametros)

        await api.post(
          `wsAutenticacaoIprejun.rule?sys=ADZ&Entrada=${parametrosFinal}`,
        )

        const parametro2 = Base64.encode(
          `{"token": "${configData.token}",
          "cpf": "${normalizeCpf(userData.cpf)}",
          "cliente": "${configData.codCliente}"}`,
        )
        const parametrosFinal2 = Base64.encode(parametro2)

        const response2 = await api.post(
          `wsDadosAdesaoIprejun.rule?sys=ADZ&Entrada=${parametrosFinal2}`,
        )

        const apiData = response2.data?.[0] || {}

        const mappedUser = mapUserData(apiData)
        const mappedParticipants = mapParticipants(apiData.dependentes ?? [])
        const mappedServiceTime = mapServiceTime(apiData.temposServico ?? [])

        setUserData((prev) => ({
          ...prev,
          ...mappedUser,
          name: mappedUser.name || prev.name,
          birthdate: mappedUser.birthdate || prev.birthdate,
          cpf: mappedUser.cpf || prev.cpf,
          email: mappedUser.email || prev.email,
          phone: mappedUser.phone || prev.phone,
          birthPlace: mappedUser.birthPlace || prev.birthPlace,
        }))

        setParticipantsGroup(mappedParticipants)
        setServiceTimeGroup(mappedServiceTime)

        navigate('/personal-info')
      } catch (res: unknown) {
        if (res instanceof Error) {
          if (res?.message === 'Network Error') {
            setErroProps({
              title: res.message,
              description: 'Falha na conexão como servidor',
            })
            navigate('/erro')
            return
          }

          if (res?.message === 'Request failed with status code 500') {
            setErroProps({
              title: 'Erro interno no servidor',
              description: res.message,
            })
            navigate('/erro')
            return
          }
        }
        setOpen(true)
        lastSubmittedCodeRef.current = ''
        helpers.setFieldValue('codeSMS', '', false)
      } finally {
        helpers.setSubmitting(false)
        isSubmittingRef.current = false
      }
    },
    [
      configData.codCliente,
      configData.token,
      navigate,
      setErroProps,
      setParticipantsGroup,
      setServiceTimeGroup,
      setUserData,
      sizeCode,
      userData.cpf,
    ],
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev > 1) return prev - 1
        if (prev === 1) {
          setReSend(true)
          return 0
        }
        return prev
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const handleReSend = useCallback(async () => {
    const parametrosGet = Base64.encode(
      `{"versao":"${configData.tipo}",
        "plano": "${configData.plano}",
        "cliente":"${configData.codCliente}",
        "cpf":"${normalizeCpf(userData.cpf)}",
        "email":"${userData.email === undefined ? '' : userData.email}",
        "telefone":"${
          userData.phone === undefined
            ? ''
            : `55${userData.phone.replace('(', '').replace(') ', '').replace('-', '')}`
        }",
        "nascimento":"${userData.birthdate}",
        "envio":"${tipoContato}"}`,
    )

    const parametrosFinalGet = Base64.encode(parametrosGet)

    await api
      .get(`wsAutenticacaoIprejun.rule?sys=ADZ&Entrada=${parametrosFinalGet}`)
      .then((res) => setConfigData({ ...configData, token: res.data.token }))
      .catch((res) => {
        if (res.message === 'Network Error') {
          setErroProps({
            title: res.message,
            description: 'Falha na conexão como servidor',
          })
          navigate('/erro')
        } else if (res.message === 'Request failed with status code 500') {
          setErroProps({
            title: 'Erro interno no servidor',
            description: res.message,
          })
          navigate('/erro')
        } else {
          alert('Ops, algo deu errado. Tente novamente mais tarde.')
        }
      })
    navigate('/register/confirm-sms')
    setOpen(false)
    setReSend(false)
    setTimer(60)
    lastSubmittedCodeRef.current = ''
  }, [configData, navigate, setConfigData, setErroProps, tipoContato, userData])

  return (
    <>
      <Header />
      <div className="mx-auto mb-[10px] flex max-w-[600px] flex-col items-center justify-center px-[5px] max-[766px]:max-w-[500px]">
        <div className="flex w-full flex-col items-center justify-center rounded-[8px] bg-white p-[15px_0] shadow-[0px_2px_4px_rgba(0,0,0,0.05),0px_6px_12px_rgba(61,69,67,0.05)] mx-[5px]">
          <span className="mt-[10px] w-[350px] text-center text-[16px] text-[#333]">
            <strong className="font-bold">
              {userData?.name?.split(' ')?.[0] ?? ''}
            </strong>
            , digite aqui o código de verificação que acabamos de te enviar via{' '}
            {contato}
          </span>
          <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            {({ values, setFieldValue, submitForm, isSubmitting }) => (
              <Form className="my-[25px]">
                <InputSMSCode
                  name="codeSMS"
                  size={sizeCode}
                  value={values.codeSMS}
                  disabled={isSubmitting}
                  onChange={(val) => {
                    setOpen(false)
                    setFieldValue('codeSMS', val)
                  }}
                  onComplete={(code) => {
                    const normalized = (code ?? '').trim()
                    if (normalized.length !== sizeCode) return
                    if (isSubmitting) return
                    if (lastSubmittedCodeRef.current === normalized) return
                    lastSubmittedCodeRef.current = normalized
                    submitForm()
                  }}
                />
              </Form>
            )}
          </Formik>
          <Collapse in={open}>
            <div className="relative mb-[12px] flex flex-col items-center rounded-[4px] bg-[#fddede] p-[8px] font-bold text-[#C53030]">
              <div className="mb-[5px] flex flex-row gap-2 items-center">
                <FiAlertCircle className="text-[20px]" />
                <p>Código incorreto!</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="border-0 bg-transparent text-[12px] text-[#333]"
              >
                Ok
              </button>
            </div>
          </Collapse>
          <span className="my-[12px] text-[12px] text-[#0072bb] min-[766px]:text-[14px]">
            00:{timer < 10 ? `0${timer}` : timer}
          </span>
          <Button
            onClick={handleReSend}
            type="button"
            color="blue"
            disabled={!reSend}
          >
            Reenviar
          </Button>
          <p className="w-[230px] text-center text-[14px] leading-[22px]">
            Clique em <strong className="text-[14px]">reenviar</strong> para
            receber um novo código
          </p>
          <Link
            to="/register"
            className="mt-[10px] flex flex-col items-center justify-center p-[6px] text-[12px] text-[#0072bb] transition-all duration-200 hover:text-[#333] focus:underline min-[766px]:p-[8px] min-[766px]:text-[14px]"
          >
            Quero trocar a forma de contato
          </Link>
        </div>
      </div>
    </>
  )
}

export default ConfirmSMS
